import { useState, useMemo, useCallback } from "react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import { Buffer } from "buffer";

import {
  SurveyEngine,
  SurveyEventBuilder,
  TranslationKey,
} from "@neuromotion-inc/mightier-survey-engine";

import { makeSurveyConfigQueryOpts } from "./survey-api-queries";
import { useSurveyStateStore } from "./survey-state-local-store";
import {
  useSurveyAnalytic,
  useTrackSurveyLoaded,
  type SurveyEventAdditionalContext,
} from "./survey-analytics";

import { getSessionUuid } from "@utils/sessionUtils";

// Utility type to strip the last parameter off a given function type
type StripLastParameter<T> = T extends (...args: infer Params) => infer Return
  ? Params extends [...infer Rest, unknown]
    ? (...args: Rest) => Return
    : never
  : never;

export type SurveyEngineIface = Pick<
  SurveyEngine,
  | "recordResponse"
  | "toJsonRepresentation"
  | "getResponseForQuestion"
  | "getStepByIdx"
  | "stepsAndQuestionsToDisplay"
  | "surveyConfigId"
  | "surveyConfigVersion"
  | "hasStepBeenSubmitted"
  | "hasBeenStarted"
  | "startPageConfig"
  | "endingPageConfig"
> & {
  getContextualizedText: StripLastParameter<
    SurveyEngine["getContextualizedText"]
  >;
  getTextForChoiceLabel: StripLastParameter<
    SurveyEngine["getTextForChoiceLabel"]
  >;
  submitStep: (
    stepId: string,
    extraContext: SurveyEventAdditionalContext,
  ) => Promise<void>;
  sendAnalyticInBackground: ReturnType<
    typeof useSurveyAnalytic
  >["sendAnalyticInBackground"];
  eventBuilder: SurveyEventBuilder;
  translationContext: SurveyEngine["context"];
  // NOTE: only use this for debugging purposes, e.g. to console log engine internals
  __debug__realEngineInstance: SurveyEngine;
};

export const useSurveyEngine = (
  surveyId: string,
  meta: string,
): SurveyEngineIface => {
  // NOTE: suspense query used, that means we don't need to handle `undefined` data
  // isLoading, isError, etc. states since those are handled by closes <Suspense> boundary
  // instead. Good news - it's easiest to use the ones in the Route components (tanstack router)!!

  const { data: surveyConfigAndContext } = useSuspenseQuery(
    makeSurveyConfigQueryOpts(surveyId, meta),
  );

  const { getSurveyState } = useSurveyStateStore();

  const [surveyEngine, setSurveyEngine] = useState<SurveyEngine>(() => {
    // Get survey state for this survey ID and version
    const currentSurveyState = getSurveyState(
      surveyConfigAndContext.survey_config.id,
      surveyConfigAndContext.survey_config.version,
    );

    // If a local storage version exists, return the survey engine instance with local storage's data
    if (currentSurveyState) {
      return SurveyEngine.fromJsonRepresentation(
        currentSurveyState.surveyEngineJSONRepresentationStr,
      );
    }

    // Otherwise, return a new survey engine instance
    return new SurveyEngine(
      surveyConfigAndContext.survey_config,
      uuidv4(),
      surveyConfigAndContext.initial_context,
    );
  });

  const { performStepSubmission, sendAnalyticInBackground } = useSurveyAnalytic(
    surveyEngine,
    meta,
  );

  const { t, i18n } = useTranslation("mightier_survey_lib");

  const _resetSurvey = useCallback(() => {
    const currState = surveyEngine.toJsonRepresentation();
    const replacement = SurveyEngine.fromJsonRepresentation(currState);
    setSurveyEngine(replacement);
  }, [surveyEngine]);

  /**
   * EVENTS
   */
  const locale = useMemo(() => i18n.language, [i18n.language]);
  const userId = useMemo(() => {
    const parsed = JSON.parse(Buffer.from(meta, "base64").toString("utf-8"));
    return parsed.user_id;
  }, [meta]);

  const surveyEventBuilder = useMemo(() => {
    return new SurveyEventBuilder(surveyEngine, t, {
      locale,
      meta,
      userId,
      sessionId: getSessionUuid(),
    });
  }, [locale, meta, userId, surveyEngine, t]);

  /**
   * CONFIG ACCESSORS
   */

  const getStepByIdx = useCallback(
    (idx: number) => {
      return surveyEngine.getStepByIdx(idx);
    },
    [surveyEngine],
  );

  const toJsonRepresentation = useCallback(() => {
    return surveyEngine.toJsonRepresentation();
  }, [surveyEngine]);

  const getResponseForQuestion = useCallback(
    (questionId: string) => {
      return surveyEngine.getResponseForQuestion(questionId);
    },
    [surveyEngine],
  );

  const surveyConfigId = useMemo(
    () => surveyEngine.surveyConfigId,
    [surveyEngine],
  );
  const surveyConfigVersion = useMemo(
    () => surveyEngine.surveyConfigVersion,
    [surveyEngine],
  );

  /**
   * GET TRANSLATED TEXT
   */

  const getContextualizedText = useCallback(
    (translationKey: TranslationKey) => {
      return surveyEngine.getContextualizedText(translationKey, t);
    },
    [surveyEngine, t],
  );

  const getTextForChoiceLabel = useCallback(
    (questionId: string, choiceValue: string) => {
      return surveyEngine.getTextForChoiceLabel(questionId, choiceValue, t);
    },
    [surveyEngine, t],
  );

  /**
   * SUBMIT
   */

  const recordResponse = useCallback<SurveyEngine["recordResponse"]>(
    (response) => {
      surveyEngine.recordResponse(response);
      _resetSurvey();
    },
    [surveyEngine, _resetSurvey],
  );

  const submitStep = useCallback(
    async (stepId: string, extraContext: SurveyEventAdditionalContext) => {
      await surveyEngine.submitStep(stepId);
      // NOTE: this is synchronous (via awaiting mutateAsync) IF the step has any questions w/ RPCs in it.
      // e.g. it waits to create player, and shows user any errors.
      // However if there are no RPCs, it calls `mutate` which does the work in the background so user can immediately proceed
      await performStepSubmission(stepId, extraContext);
    },
    [surveyEngine, performStepSubmission],
  );

  const hasStepBeenSubmitted = useCallback(
    (stepId: string) => {
      return surveyEngine.hasStepBeenSubmitted(stepId);
    },
    [surveyEngine],
  );

  const stepsAndQuestionsToDisplay = useMemo(() => {
    return surveyEngine.stepsAndQuestionsToDisplay;
  }, [surveyEngine]);

  /**
   * send SurveyInstanceLoaded analytic on page load
   */
  useTrackSurveyLoaded(
    surveyConfigId,
    surveyEventBuilder,
    sendAnalyticInBackground,
  );

  return useMemo(() => {
    const ret: SurveyEngineIface = {
      // CONFIG ACCESSORS
      getStepByIdx,
      toJsonRepresentation,
      stepsAndQuestionsToDisplay,
      startPageConfig: surveyEngine.startPageConfig,
      endingPageConfig: surveyEngine.endingPageConfig,
      // GET TRANSLATED TEXT
      getContextualizedText,
      getTextForChoiceLabel,
      // SUBMIT
      recordResponse,
      submitStep,
      hasStepBeenSubmitted,
      hasBeenStarted: surveyEngine.hasBeenStarted,
      // OTHER ANALYTICS
      eventBuilder: surveyEventBuilder,
      sendAnalyticInBackground,
      // trackInstanceCompleted,
      // GET ANSWER
      getResponseForQuestion,
      // CONFIG DATA
      surveyConfigId,
      surveyConfigVersion,
      //
      translationContext: surveyEngine.context,
      __debug__realEngineInstance: surveyEngine,
    };
    return ret;
  }, [
    getStepByIdx,
    toJsonRepresentation,
    stepsAndQuestionsToDisplay,
    //
    getContextualizedText,
    getTextForChoiceLabel,
    //
    recordResponse,
    submitStep,
    hasStepBeenSubmitted,
    // surveyEngine.hasBeenStarted,
    // trackInstanceCompleted,
    surveyEventBuilder,
    sendAnalyticInBackground,
    //
    getResponseForQuestion,
    //
    surveyConfigId,
    surveyConfigVersion,
    //
    // surveyEngine.context,
    //
    surveyEngine,
  ]);
};
